import { format, subDays } from 'date-fns';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { authProvidersOptions as fetchAuthProvidersOptions } from '@/api/auth';
import {
  ABTestsCMS as fetchABTestsCMS,
  bonusesCMS as fetchBonusesCMS,
  brandAssetsCMS as fetchBrandAssetsCMS,
  fetchReferralProgram,
  fetchScratchGame,
  gameCategoriesCMS as fetchCategoriesCMS,
  gameCoversCMS as fetchGameCoversCMS,
  gameThemesCMS as fetchGameThemesCMS,
  MethodGroupType,
  mobileAppCMS as fetchMobileAppCMS,
  paymentMethodsCMS as fetchPaymentMethodsCMS,
  registrationBannerCMS as fetchRegistrationBannerCMS,
  registrationSlidesCMS as fetchRegistrationSlidesCMS,
  sliderCMS as fetchSliderCMS,
  starWinnersCMS as fetchStarWinnersCMS,
  topSpotGameCMS as fetchTopSpotGameCMS,
} from '@/api/cms';
import { playerFields as fetchPlayerFields } from '@/api/common';
import { playerSettings as fetchPlayerSettings } from '@/api/common';
import { TransactionState, userLimits as fetchUserLimits } from '@/api/profile';
import { isIos } from '@/helpers/common';
import { fetchToStore } from '@/helpers/fetchers';
import { groupMemberNoDeposits } from '@/helpers/user';
import {
  InvreasedPrevDepEnum,
  playerPaymentsWithPages as fetchPlayerPaymentsWithPages,
} from '@/modules/deposit';
import { tournamentGame as fetchTournamentGame } from '@/modules/tournaments/api';
import { useCommonStore, useLocalStorageStore } from '@/store';
import { GameCovers } from '@/store/commonStore/types';

export const useInitStore = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const {
    isAuthenticated,
    pendingUser,
    user,
    currentWallet,
    lastSuccessfulPaymentsByCurrency,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      user: state.user,
      currentWallet: state.currentWallet,
      lastSuccessfulPaymentsByCurrency: state.lastSuccessfulPaymentsByCurrency,
    }))
  );

  useEffect(() => {
    try {
      fetchToStore(fetchPlayerFields, 'playerFields');
      fetchGameCoversCMS().then(({ data }) => {
        const covers = (data || []).reduce(
          (acc, { gameId, cover, gradientColor }) => {
            acc[gameId] = { cover, gradientColor };
            return acc;
          },
          {} as GameCovers
        );
        useCommonStore.setState({ covers });
      });
      fetchToStore(fetchBrandAssetsCMS, 'brandAssets');
      fetchToStore(fetchABTestsCMS, 'cmsABTests');
      fetchTournamentGame().then(({ data }) => {
        if (!data) return;
        const { tournamentGameCategories, tournamentGameIds } = data;
        useCommonStore.setState({
          tournamentGameCategories: tournamentGameCategories
            ? tournamentGameCategories.split(',').map((item) => item.trim())
            : [],
          tournamentGameIds: tournamentGameIds
            ? tournamentGameIds.split(',').map((item) => item.trim())
            : [],
        });
      });

      useCommonStore.setState({ mobileAppCMSPending: true });
      fetchMobileAppCMS()
        .then(({ data }) => {
          if (!data) return;
          const { showIos, showAndroid, showPwa } = data;
          useCommonStore.setState({
            mobileAppCMS: data,
            isNoAppsMobile: isIos() ? !showIos && !showPwa : !showAndroid,
            isAnyAppsDesktop: showIos || showAndroid || showPwa,
          });
        })
        .finally(() => {
          useCommonStore.setState({ mobileAppCMSPending: false });
        });
    } catch (error: unknown) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    try {
      fetchToStore(() => fetchPaymentMethodsCMS(language), 'cmsPaymentMethods');
      fetchToStore(() => fetchSliderCMS(language), 'slidesData');
      fetchRegistrationSlidesCMS(language).then(({ data }) => {
        const regSlides = (() => {
          if (!data) return [];
          return data
            .map(({ order, ...rest }) => ({ order: order ?? 1, ...rest }))
            .sort((a, b) => a.order - b.order);
        })();
        useCommonStore.setState({ regSlides });
      });
      fetchToStore(
        () => fetchRegistrationBannerCMS(language),
        'registrationBannerData'
      );
      fetchToStore(() => fetchCategoriesCMS(language), 'categoriesCMS');
      fetchToStore(() => fetchBonusesCMS(language), 'cmsBonuses');
      fetchToStore(() => fetchStarWinnersCMS(language), 'starWinners');
      fetchToStore(() => fetchTopSpotGameCMS(language), 'topSpotCMSData');
      fetchToStore(() => fetchGameThemesCMS(language), 'gameThemes');
      fetchReferralProgram(language);
      fetchScratchGame(language);
    } catch (error: unknown) {
      console.log(error);
    }
  }, [language]);

  useEffect(() => {
    try {
      if (isAuthenticated) {
        fetchToStore(fetchUserLimits, 'limits');
      }
      if (!isAuthenticated) {
        fetchToStore(
          fetchAuthProvidersOptions,
          'authProvidersOptions',
          'pendingAuthProvidersOptions'
        );
        return;
      }
    } catch (error: unknown) {
      console.log(error);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    try {
      if (pendingUser) return;

      fetchToStore(fetchPlayerSettings, 'playerSettings');
    } catch (error: unknown) {
      console.log(error);
    }
  }, [pendingUser, isAuthenticated]);

  const isInvreasedPrevDepAB = useLocalStorageStore.getState().get<{
    name: InvreasedPrevDepEnum;
  }>('ab-testing__exp_increased_prev_dep_lu');

  useEffect(() => {
    if (
      !user ||
      groupMemberNoDeposits(user) ||
      currentWallet?.isCrypto ||
      lastSuccessfulPaymentsByCurrency[user.currency] !== undefined ||
      isInvreasedPrevDepAB?.name === InvreasedPrevDepEnum.A
    )
      return;
    useCommonStore.setState({ lastSuccessfulPaymentsByCurrencyPending: true });

    const to = format(new Date(), 'yyyy-MM-dd');
    const from = format(subDays(new Date(), 10), 'yyyy-MM-dd');

    const controller = new AbortController();
    const signal = controller.signal;

    setTimeout(() => controller.abort(), 5000);

    fetchPlayerPaymentsWithPages(
      {
        page: 1,
        filter: {
          currency: user.currency,
          type: MethodGroupType.DEPOSIT,
          from,
          to,
        },
      },
      signal
    )
      .then(({ data }) => {
        if (!data) return;
        const transaction = data.data.find(
          ({ state, currency }) =>
            state === TransactionState.Succeeded && currency === user.currency
        );
        useCommonStore.setState({
          lastSuccessfulPaymentsByCurrency: {
            ...lastSuccessfulPaymentsByCurrency,
            [user.currency]: transaction?.amountCents || 0,
          },
        });
      })
      .finally(() => {
        useCommonStore.setState({
          lastSuccessfulPaymentsByCurrencyPending: false,
        });
      });
  }, [user, currentWallet, lastSuccessfulPaymentsByCurrency]);

  return null;
};
